import _ from 'lodash'
import {UUID} from '@/plugins/common'
import {getProperties} from './componentDefaults.js'
import {relationTypes} from './schema'
import {UUIDTime} from '@/plugins/uuid-time'

export default class Component {
  id
  componentId
  alias
  name
  componentType
  // text, numical, identity, datetime
  archive = {
    defaultArchivedId: UUID.create(),
    tableId: undefined,
    columnId: undefined,
    relationId: undefined,
    primaryKey: undefined,
    foreignKey: undefined
  }
  properties

  constructor (args = {}) {
    if (!args.componentType) {
      throw new Error(`component-type cannot be null.`)
    }

    this.id = args.id || UUID.create()
    // 这个地方不能使用控件 ID, 不然会被替换掉
    this.componentId = args.componentId || UUIDTime.create()
    this.alias = args.alias
    let defaultProperties = getProperties(args.componentType).defaults.properties
    defaultProperties = typeof args.properties === 'string' ? _.merge(defaultProperties, JSON.parse(args.properties)) : _.merge(defaultProperties, args.properties)
    // 统一处理历史 properties
    const disposeProperties = this.disposeHistoryProperties(defaultProperties, args)
    const defaults = {
      name: args.name,
      archive: args.archive,
      properties: disposeProperties
    }
    // 获取处理过后的返回数据
    _.defaultsDeep(this, defaults, args)
    if (args.children) {
      this.children = args.children.map(m => {
        if (m instanceof Component) {
          return m
        } else {
          return Component.create(m, args)
        }
      })
    }
  }

  static create (args, parent) {
    if (!args.componentType) {
      throw new Error(`component-type cannot be null.`)
    }

    let properties = getProperties(args.componentType)

    let component
    // 创建对应类型的组件
    switch (properties.ctor) {
      case 'ColumnComponent':
        component = new ColumnComponent(args, parent.archive.tableId)
        break
      case 'TableComponent':
        component = new TableComponent(args)
        break
    }
    return component
  }

  recursionGetComponent (id) {
    let component
    this.recursion(m => {
      if (m.id === id) {
        component = m

        return true
      }
    })

    return component
  }

  // 向上递归, 找出当前控件的所有的祖先元素
  recursionGetSuperiors (id) {
    let superiors = []
    let parent = this.recursionGetParentComponent(id)
    if (parent) {
      superiors.push(parent)
      parent.children.forEach(m => {
        if (m.id !== id) {
          superiors.push(m)
        }
      })
      superiors = _.concat(superiors, this.recursionGetSuperiors(parent.id))
    }
    return superiors
  }

  recursionGetParentComponent (id) {
    let component
    this.recursion(m => {
      if (m.children && m.children.some(n => n.id === id)) {
        component = m
        return true
      }
    })
    return component
  }

  // 获取 id 控件中的归档父元素
  recursionGetArchivedParentComponent (id) {
    let component
    this.recursion(() => {
      let parentComponent = this.recursionGetParentComponent(id)
      if (!parentComponent) {
        return true
      } else if (parentComponent.archive.enable) {
        component = parentComponent
        return true
      } else {
        id = parentComponent.id
      }
    })
    return component
  }

  recursionGetComponents () {
    let children = []

    this.recursion((component) => children.push(component))

    return children
  }

  recursion (callbackFn) {
    // 当回调函数返回值为 true 时，结束递归
    if (callbackFn(this) === true) {
      return
    }

    // 如果存在子组件，则遍历子组件递归调用
    if (this.children) {
      this.children.forEach(m => m.recursion(callbackFn))
    }
  }

  recursion1 (action, callbck) {
    // 当回调函数返回值为 true 时，结束递归
    if (action(this) === true) {
      callbck(this)
      return true
    }

    // 如果存在子组件，则遍历子组件递归调用
    if (this.children) {
      return this.children.some(m => m.recursion1(action, callbck))
    }
  }

  some (action) {
    let result = false

    this.recursion1(action, () => {
      result = true
    })

    return result
  }

  find (action) {
    let result
    this.recursion1(action, m => {
      result = m
    })

    return result
  }

  get archivedChildren () {
    let children = []
    if (this.children) {
      this.children.forEach(m => {
        if (m.archive.enable) {
          children.push(m)
        } else {
          children = _.concat(children, m.archivedChildren)
        }
      })
    }
    return children
  }
  // 获取全部控件
  get allChildren () {
    let children = []
    if (this.children) {
      this.children.forEach(m => {
        if (m.componentType === 'rc-table') {
          children.push(m)
          children = _.concat(children, m.allChildren)
        } else if (m.componentType !== 'rc-draggable' && m.componentType !== 'rc-layout' && m.componentType !== 'rc-form' && m.componentType !== 'rc-group') {
          children.push(m)
        } else {
          children = _.concat(children, m.allChildren)
        }
      })
    }
    return children
  }
  /**
   * 处理历史 properties 中的字段。处理历史的 properties 统一放到这个方法中修改
   * @param {Object} properties 需要处理的 properties
   * @param {Object} component 处理的控件
   * */
  disposeHistoryProperties (properties, component) {
    if (component.componentType === 'rc-group') {
      // NOTE: 62期处理分组
      if (typeof properties.defaultFold === 'boolean') {
        properties.defaultFold = properties.defaultFold ? 'canFoldingAndDefaultFolding' : 'canFoldingAndDefaultOpen'
      }
    }
    return properties
  }
}

export class TableComponent extends Component {
  constructor (args) {
    super(args)

    if (args.archive.enable) {
      const defaults = {
        archive: {
          enable: true,
          tableId: this.archive.defaultArchivedId,
          primaryKey: '',
          foreignKey: 'create'
        }
      }
      _.defaults(this.archive, defaults.archive)
    }
    if (!this.children) {
      this.children = []
    }
  }

  get relationType () {
    return relationTypes.ONE2MANY
  }

  changeArchived (schema) {
    schema.createOrSetTable(this)
    // 修改表的归档之后, 将子控件的的归档信息全部修改成默认的归档字段
    this.children.forEach(el => {
      if (el instanceof ColumnComponent) {
        el.tableId = this.tableId
        el.changeArchived(schema)
      }
    })
  }
}

export class ColumnComponent extends Component {
  constructor (args, tableId) {
    super(args)

    const defaults = {
      archive: {
        enable: true,
        tableId: tableId,
        columnId: this.archive.defaultArchivedId
      }
    }

    _.defaults(this.archive, defaults.archive)
  }

  get relationType () {
    if (this.componentType === 'rc-combobox') {
      return relationTypes.MANY2ONE
    }
  }
}

export class FormComponent extends TableComponent {
  constructor (args) {
    super(args)
    _.defaults(this, args)
  }

  get relationType () {
    return relationTypes.ONE2ONE
  }

  static create (args) {
    const form = getProperties('rc-form').defaults
    const layout = getProperties('rc-layout').defaults
    const draggable = getProperties('rc-draggable').defaults
    const defaults = args ? _.defaults(args, form) : form
    layout.name = '双列布局'
    layout.children = [draggable, draggable]
    defaults.children = [layout]
    return new FormComponent(defaults)
  }

  static load (args) {
    return new FormComponent(args)
  }
}

export class ProcessComponent extends TableComponent {
  constructor (args) {
    super(args)
    _.defaults(this, args)
  }

  static create (args) {
    const defaults = getProperties('rc-process')
    return new ProcessComponent(defaults.defaults)
  }

  static load (args) {
    return new ProcessComponent(args)
  }
}



// WEBPACK FOOTER //
// ./src/store/form/component.js